import React, {useState, useEffect} from 'react';
import {
  Box,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import {useNavigate, useParams} from 'react-router-dom';
import {httpRequest} from '../../@crema/services/auth/jwt-auth';

// Import components
import OverviewTab from './components/OverviewTab';
import { formatDateToJerusalem } from '../../utils/dateFormatter';
import StatisticsTab from './components/StatisticsTab';
import DailyReportsTab from './components/DailyReportsTab';
import ApiDocumentationTab from './components/ApiDocumentationTab';
import SettingsTab from './components/SettingsTab';

// Import dialogs
import EditDialog from './components/EditDialog';
import DeleteDialog from './components/DeleteDialog';
import LivePeriodDialog from './components/LivePeriodDialog';

// Import utilities
import {isExpired, createCSVContent} from './utils/helpers';

const ApiCredentialDetails = () => {
  const navigate = useNavigate();
  const {id} = useParams();

  // Main state
  const [credential, setCredential] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Dialog states
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openLivePeriodDialog, setOpenLivePeriodDialog] = useState(false);

  // Loading states
  const [exportLoading, setExportLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Statistics state
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Daily reports state
  const [dailyStats, setDailyStats] = useState([]);
  const [dailyStatsLoading, setDailyStatsLoading] = useState(false);
  const [selectedDays, setSelectedDays] = useState(7);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Edit form state
  const [editForm, setEditForm] = useState({
    credential_name: '',
    // integration_source removed
    username: '',
    password: '',
    live_period_type: 'forever',
    live_duration_days: null,
    live_until_date: '',
    is_active: true,
    is_system_file_upload: false,
  });

  // Live period form state
  const [livePeriodForm, setLivePeriodForm] = useState({
    live_period_type: 'forever',
    live_duration_days: null,
    live_until_date: '',
  });

  const integrationSources = ['zapier', 'make', 'opal', 'n8n', 'customCurl'];

  useEffect(() => {
    fetchCredentialDetails();
  }, [id]);

  useEffect(() => {
    if (credential) {
      fetchStats();
      fetchDailyStats(); // Load daily stats automatically
    }
  }, [credential]);

  useEffect(() => {
    if (tabValue === 2 && credential) {
      fetchDailyStats(1);
    }
  }, [tabValue, credential]);

  // Refetch when selectedDays changes (only when on Reports tab)
  useEffect(() => {
    if (tabValue === 2 && credential && id) {
      setPagination((prev) => ({...prev, currentPage: 1}));
      fetchDailyStats(1);
    }
  }, [selectedDays, tabValue]);

  const fetchCredentialDetails = async () => {
    try {
      setLoading(true);
      const response = await httpRequest(`api-credentials/${id}`, {
        method: 'GET',
      });
      const credentialData = response.data.data;
      // Convert number to boolean for is_system_file_upload
      const normalizedCredential = {
        ...credentialData,
        is_system_file_upload:
          !!credentialData.is_system_file_upload ||
          credentialData.is_system_file_upload === 1,
        is_active: !!credentialData.is_active || credentialData.is_active === 1,
      };
      setCredential(normalizedCredential);
      setEditForm({
        credential_name: credentialData.credential_name,
        // integration_source removed - using credential_name instead
        username: credentialData.username || '',
        password: credentialData.password || '',
        live_period_type: credentialData.live_period_type || 'forever',
        live_duration_days: credentialData.live_duration_days,
        live_until_date: credentialData.live_until_date || '',
        is_active: normalizedCredential.is_active,
        is_system_file_upload: normalizedCredential.is_system_file_upload,
      });
      setLivePeriodForm({
        live_period_type: response.data.data.live_period_type || 'forever',
        live_duration_days: response.data.data.live_duration_days,
        live_until_date: response.data.data.live_until_date || '',
      });
    } catch (err) {
      setError('Failed to fetch credential details');
      console.error('Error fetching credential details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const response = await httpRequest(`test-stats/${id}`, {
        method: 'GET',
      });
      setStats(response.data.data);
    } catch (err) {
      setError('Failed to fetch statistics');
      console.error('Error fetching statistics:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchDailyStats = async (page = 1) => {
    try {
      setDailyStatsLoading(true);
      setError(''); // Clear any previous errors

      const params = new URLSearchParams({
        days: selectedDays.toString(),
        page: page.toString(),
        limit: itemsPerPage.toString(),
      });

      if (searchTerm && searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      if (startDate) {
        params.append('startDate', startDate);
      }
      if (endDate) {
        params.append('endDate', endDate);
      }

      console.log('Fetching daily stats with params:', params.toString());

      const response = await httpRequest(
        `test-daily-stats/${id}?${params.toString()}`,
        {method: 'GET'},
      );

      console.log('Daily stats response:', response.data);

      // Handle different response structures
      if (response.data) {
        // Check if response has success flag
        if (response.data.success !== undefined) {
          if (response.data.success && response.data.data) {
            const dataArray = Array.isArray(response.data.data)
              ? response.data.data
              : response.data.data.data || [];
            setDailyStats(dataArray);
            // Use backend pagination if available, otherwise create default
            const backendPagination = response.data.data.pagination;
            setPagination(
              backendPagination || {
                currentPage: page,
                totalPages: 1,
                totalItems: dataArray.length,
                itemsPerPage: itemsPerPage,
                hasNextPage: false,
                hasPrevPage: false,
              },
            );
          } else {
            setDailyStats([]);
            setPagination({
              currentPage: page,
              totalPages: 1,
              totalItems: 0,
              itemsPerPage: itemsPerPage,
              hasNextPage: false,
              hasPrevPage: false,
            });
          }
        } else if (response.data.data) {
          // Response without success flag but has data
          const dataArray = Array.isArray(response.data.data)
            ? response.data.data
            : response.data.data.data || [];
          setDailyStats(dataArray);
          setPagination(
            response.data.pagination ||
              response.data.data?.pagination || {
                currentPage: page,
                totalPages: 1,
                totalItems: dataArray.length,
                itemsPerPage: itemsPerPage,
                hasNextPage: false,
                hasPrevPage: false,
              },
          );
        } else {
          setDailyStats([]);
          setPagination({
            currentPage: page,
            totalPages: 1,
            totalItems: 0,
            itemsPerPage: itemsPerPage,
            hasNextPage: false,
            hasPrevPage: false,
          });
        }
      } else {
        setDailyStats([]);
        setPagination({
          currentPage: page,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: itemsPerPage,
          hasNextPage: false,
          hasPrevPage: false,
        });
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to fetch daily statistics';
      setError(errorMessage);
      console.error('Error fetching daily statistics:', err);
      setDailyStats([]);
      setPagination({
        currentPage: page,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: itemsPerPage,
        hasNextPage: false,
        hasPrevPage: false,
      });
    } finally {
      setDailyStatsLoading(false);
    }
  };

  // Event handlers
  const handleEdit = () => setOpenEditDialog(true);
  const handleDelete = () => setOpenDeleteDialog(true);
  const handleLivePeriod = () => setOpenLivePeriodDialog(true);

  const handleSaveEdit = async () => {
    try {
      await httpRequest(`api-credentials/${id}`, {
        method: 'PUT',
        body: editForm,
      });
      setOpenEditDialog(false);
      fetchCredentialDetails();
    } catch (err) {
      setError('Failed to update credential');
      console.error('Error updating credential:', err);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await httpRequest(`api-credentials/${id}`, {
        method: 'DELETE',
      });
      setOpenDeleteDialog(false);
      navigate('/api-credentials/all');
    } catch (err) {
      setError('Failed to delete credential');
      console.error('Error deleting credential:', err);
    }
  };

  const handleSaveLivePeriod = async () => {
    try {
      await httpRequest(`api-credentials/${id}/live-period`, {
        method: 'PUT',
        body: livePeriodForm,
      });
      setOpenLivePeriodDialog(false);
      fetchCredentialDetails();
    } catch (err) {
      setError('Failed to update live period');
      console.error('Error updating live period:', err);
    }
  };

  const handleToggleActive = async () => {
    try {
      await httpRequest(`test-toggle-active/${id}`, {
        method: 'PUT',
        body: {
          is_active: !credential.is_active,
        },
      });
      fetchCredentialDetails();
    } catch (err) {
      setError('Failed to toggle active status');
      console.error('Error toggling active status:', err);
    }
  };

  // Daily reports handlers
  const handlePageChange = (newPage) => {
    fetchDailyStats(newPage);
  };

  const handleSearch = () => {
    setPagination((prev) => ({...prev, currentPage: 1}));
    fetchDailyStats(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setSelectedDays(7); // Reset to initial value
    setItemsPerPage(10);
    setPagination((prev) => ({...prev, currentPage: 1}));
    // fetchDailyStats will be called by the selectedDays useEffect
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    fetchDailyStats(1);
  };

  const handleExportDailyExcel = async () => {
    try {
      setExportLoading(true);
      const url = `api-credentials/${id}/export-daily-excel?days=${selectedDays}`;
      const response = await httpRequest(url, {
        method: 'GET',
        headers: {
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      });

      // Create download link
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      const filename = `daily-statistics-${credential.credential_name}-${selectedDays}days.xlsx`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      setError('Failed to export daily Excel');
      console.error('Error exporting daily Excel:', err);
    } finally {
      setExportLoading(false);
    }
  };

  const handleDownloadDayData = async (date) => {
    try {
      setExportLoading(true);
      console.log('Downloading data for date:', date);
      const response = await httpRequest(`test-daily-data/${id}?date=${date}`, {
        method: 'GET',
      });

      if (response.data.success) {
        const {validData, invalidData} = response.data.data;
        console.log(
          'Download response - Valid data:',
          validData.length,
          'Invalid data:',
          invalidData.length,
        );

        // Create CSV content (includes UTF-8 BOM for proper Arabic/Unicode display in Excel)
        const csvContent = createCSVContent(validData, invalidData, date);

        // Create download link with UTF-8 encoding
        // The BOM in csvContent ensures Excel recognizes UTF-8 encoding for Arabic/Unicode characters
        const blob = new Blob([csvContent], {
          type: 'text/csv;charset=utf-8;',
        });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;

        // Extract date part more reliably to avoid timezone issues
        const dateStr = date.includes('T') ? date.split('T')[0] : date;
        const filename = `${credential.credential_name}-data-${dateStr}.csv`;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        setError('Failed to download day data');
      }
    } catch (err) {
      setError('Failed to download day data');
      console.error('Error downloading day data:', err);
    } finally {
      setExportLoading(false);
    }
  };

  const generateSingleDocumentationHTML = () => {
    return `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Single Lead API Documentation - ${
            credential.credential_name
          }</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              line-height: 1.6;
              color: #333;
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #4caf50;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }
            .header h1 {
              color: #4caf50;
              margin: 0;
              font-size: 28px;
            }
            .header p {
              color: #666;
              margin: 5px 0 0 0;
            }
            .section {
              margin-bottom: 30px;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .section h2 {
              color: #4caf50;
              border-bottom: 1px solid #e0e0e0;
              padding-bottom: 10px;
              margin-bottom: 15px;
              page-break-after: avoid;
              break-after: avoid;
            }
            .endpoint {
              background: #f5f5f5;
              padding: 15px;
              border-radius: 5px;
              margin: 10px 0;
              font-family: 'Courier New', monospace;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .method {
              background: #4caf50;
              color: white;
              padding: 4px 8px;
              border-radius: 3px;
              font-weight: bold;
              margin-right: 10px;
            }
            .code-block {
              background: #f8f8f8;
              border: 1px solid #e0e0e0;
              border-radius: 5px;
              padding: 15px;
              margin: 10px 0;
              font-family: 'Courier New', monospace;
              font-size: 14px;
              overflow-x: auto;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .field-table {
              width: 100%;
              border-collapse: collapse;
              margin: 15px 0;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .field-table th,
            .field-table td {
              border: 1px solid #ddd;
              padding: 12px;
              text-align: left;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .field-table th {
              background-color: #f5f5f5;
              font-weight: bold;
            }
            .required {
              color: #d32f2f;
              font-weight: bold;
            }
            .optional {
              color: #666;
            }
            .footer {
              text-align: center;
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #e0e0e0;
              color: #666;
              font-size: 12px;
            }
            @media print {
              .section {
                page-break-inside: avoid;
                break-inside: avoid;
              }
              .field-table {
                page-break-inside: avoid;
                break-inside: avoid;
              }
              .code-block {
                page-break-inside: avoid;
                break-inside: avoid;
              }
              h2, h3 {
                page-break-after: avoid;
                break-after: avoid;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>🔄 Single Lead API Documentation</h1>
            <p><strong>${credential.credential_name}</strong></p>
            <p>Generated on ${formatDateToJerusalem(new Date(), 'date-only')} at ${formatDateToJerusalem(new Date(), 'time-only')}</p>
          </div>

          <div class="section">
            <h2>📡 API Endpoint</h2>
            <div class="endpoint">
              <span class="method">POST</span> https://be.blackdawn.info/api/v1/external/data-entry
            </div>
            <p><strong>Use for:</strong> Sending one lead at a time. Perfect for real-time integrations, webhooks, and form submissions.</p>
            <p><strong>Performance:</strong> ~100ms per request</p>
          </div>

          <div class="section">
            <h2>🔐 Headers</h2>
            <div class="code-block">
Content-Type: application/json<br>
X-API-Key: ${credential.api_key}<br>
X-Username: ${credential.username || 'your_username'}<br>
X-Password: ${credential.password || 'your_password'}<br>
User-Agent: YourApp/1.0
            </div>
          </div>

          <div class="section">
            <h2>📋 Request Body Structure</h2>
            <div class="code-block">
{
  "fullName": "John Doe",
  "email": "john.doe@example.com",
  "phone": "12345678900",
  "age": "30",
  "country": "United States",
  "marks": "Lead score: 85, Campaign: CAMP_001",
  "MPC_4": "High interest level",
  "other_data": {
    "lead_score": 85,
    "campaign_id": "CAMP_001",
    "interest_level": "High"
  },
  "data_source": "external_api"
}
            </div>
          </div>

          <div class="section">
            <h2>📝 Field Descriptions</h2>
            <table class="field-table">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>fullName</td>
                  <td>string</td>
                  <td><span class="required">Yes</span></td>
                  <td>Full name of the contact</td>
                </tr>
                <tr>
                  <td>email</td>
                  <td>string</td>
                  <td><span class="required">Yes</span></td>
                  <td>Email address (must be valid format)</td>
                </tr>
                <tr>
                  <td>phone</td>
                  <td>string</td>
                  <td><span class="required">Yes</span></td>
                  <td>Phone number (7-15 digits, start with 1-9, no + prefix allowed)</td>
                </tr>
                <tr>
                  <td>age</td>
                  <td>string</td>
                  <td><span class="required">Yes</span></td>
                  <td>Age of the contact</td>
                </tr>
                <tr>
                  <td>country</td>
                  <td>string</td>
                  <td><span class="required">Yes</span></td>
                  <td>Country name</td>
                </tr>
                <tr>
                  <td>marks</td>
                  <td>text</td>
                  <td><span class="required">Yes</span></td>
                  <td>Additional text data or notes about the contact</td>
                </tr>
                <tr>
                  <td>MPC_4</td>
                  <td>text</td>
                  <td><span class="required">Yes</span></td>
                  <td>Additional text data or classification</td>
                </tr>
                <tr>
                  <td>other_data</td>
                  <td>object</td>
                  <td><span class="optional">No</span></td>
                  <td>Custom data as JSON object (optional)</td>
                </tr>
                <tr>
                  <td>data_source</td>
                  <td>string</td>
                  <td><span class="optional">No</span></td>
                  <td>Source identifier (defaults to 'external_api')</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="section">
            <h2>💻 Example cURL Request</h2>
            <div class="code-block">
curl -X POST "https://be.blackdawn.info/api/v1/external/data-entry" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${credential.api_key}" \\
  -H "X-Username: ${credential.username || 'your_username'}" \\
  -H "X-Password: ${credential.password || 'your_password'}" \\
  -d '{
    "fullName": "Jane Smith",
    "email": "jane.smith@example.com",
    "phone": "19876543210",
    "age": "28",
    "country": "Canada",
    "marks": "Lead score: 92, Campaign: CAMP_2024_Q1",
    "MPC_4": "Very High interest level",
    "other_data": {
      "lead_score": 92,
      "campaign_id": "CAMP_2024_Q1",
      "interest_level": "Very High"
    },
    "data_source": "external_api"
  }'
            </div>
          </div>

          <div class="section">
            <h2>📤 Response Format</h2>
            <div class="code-block">
{
  "data": {
    "id": 12345,
    "valid": true
  },
  "message": "Data processed successfully",
  "status": 201
}
            </div>
          </div>

          <div class="section">
            <h2>❌ Error Response Format</h2>
            <div class="code-block">
{
  "data": {
    "valid": false,
    "errors": ["Invalid email format"]
  },
  "message": "Invalid data",
  "status": 400
}
            </div>
          </div>

          <div class="footer">
            <p>Single Lead API Documentation for: ${
              credential.credential_name
            }</p>
            <p>For support, please contact your system administrator.</p>
          </div>
        </body>
        </html>
      `;
  };

  const generateBulkDocumentationHTML = () => {
    return `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Bulk Lead API Documentation - ${
            credential.credential_name
          }</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              line-height: 1.6;
              color: #333;
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #1976d2;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }
            .header h1 {
              color: #1976d2;
              margin: 0;
              font-size: 28px;
            }
            .header p {
              color: #666;
              margin: 5px 0 0 0;
            }
            .section {
              margin-bottom: 30px;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .section h2 {
              color: #1976d2;
              border-bottom: 1px solid #e0e0e0;
              padding-bottom: 10px;
              margin-bottom: 15px;
              page-break-after: avoid;
              break-after: avoid;
            }
            .endpoint {
              background: #f5f5f5;
              padding: 15px;
              border-radius: 5px;
              margin: 10px 0;
              font-family: 'Courier New', monospace;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .method {
              background: #1976d2;
              color: white;
              padding: 4px 8px;
              border-radius: 3px;
              font-weight: bold;
              margin-right: 10px;
            }
            .code-block {
              background: #f8f8f8;
              border: 1px solid #e0e0e0;
              border-radius: 5px;
              padding: 15px;
              margin: 10px 0;
              font-family: 'Courier New', monospace;
              font-size: 14px;
              overflow-x: auto;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .field-table {
              width: 100%;
              border-collapse: collapse;
              margin: 15px 0;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .field-table th,
            .field-table td {
              border: 1px solid #ddd;
              padding: 12px;
              text-align: left;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .field-table th {
              background-color: #f5f5f5;
              font-weight: bold;
            }
            .required {
              color: #d32f2f;
              font-weight: bold;
            }
            .optional {
              color: #666;
            }
            .performance-highlight {
              background: #e8f5e8;
              border: 1px solid #4caf50;
              border-radius: 5px;
              padding: 15px;
              margin: 15px 0;
            }
            .footer {
              text-align: center;
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #e0e0e0;
              color: #666;
              font-size: 12px;
            }
            @media print {
              .section {
                page-break-inside: avoid;
                break-inside: avoid;
              }
              .field-table {
                page-break-inside: avoid;
                break-inside: avoid;
              }
              .code-block {
                page-break-inside: avoid;
                break-inside: avoid;
              }
              h2, h3 {
                page-break-after: avoid;
                break-after: avoid;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>📦 Bulk Lead API Documentation</h1>
            <p><strong>${credential.credential_name}</strong></p>
            <p>Generated on ${formatDateToJerusalem(new Date(), 'date-only')} at ${formatDateToJerusalem(new Date(), 'time-only')}</p>
          </div>

          <div class="section">
            <h2>📡 API Endpoint</h2>
            <div class="endpoint">
              <span class="method">POST</span> https://be.blackdawn.info/api/v1/external/bulk-data-entry
            </div>
            <p><strong>Use for:</strong> Sending multiple leads in one request. Perfect for batch imports, CSV uploads, and high-volume integrations.</p>
            <div class="performance-highlight">
              <strong>🚀 Performance:</strong> 5-10x faster than individual requests (~10ms per lead vs ~100ms per individual request)
            </div>
            <p><strong>Rate Limits:</strong> Max 1000 leads per request, 10 requests per minute per API key. Multiple systems can send data simultaneously without conflicts.</p>
          </div>

          <div class="section">
            <h2>🔐 Headers</h2>
            <div class="code-block">
Content-Type: application/json<br>
X-API-Key: ${credential.api_key}<br>
X-Username: ${credential.username || 'your_username'}<br>
X-Password: ${credential.password || 'your_password'}<br>
User-Agent: YourApp/1.0
            </div>
          </div>

          <div class="section">
            <h2>📋 Request Body Structure</h2>
            <div class="code-block">
{
  "leads": [
    {
      "fullName": "John Doe",
      "email": "john.doe@example.com",
      "phone": "12345678900",
      "age": "30",
      "country": "United States",
      "marks": "Lead score: 85, Campaign: CAMP_001",
      "MPC_4": "High interest level",
      "other_data": {
        "lead_score": 85,
        "campaign_id": "CAMP_001",
        "interest_level": "High"
      }
    },
    {
      "fullName": "Jane Smith",
      "email": "jane.smith@example.com",
      "phone": "19876543210",
      "age": "28",
      "country": "Canada",
      "marks": "Lead score: 92, Campaign: CAMP_002",
      "MPC_4": "Very High interest level",
      "other_data": {
        "lead_score": 92,
        "campaign_id": "CAMP_002",
        "interest_level": "Very High"
      }
    }
  ],
  "batch_size": 50,
  "validate_all_first": false
}
            </div>
            <p><strong>Optional Parameters:</strong></p>
            <ul>
              <li><code>batch_size</code>: Process leads in batches (default: 50, max: 1000)</li>
              <li><code>validate_all_first</code>: Validate all leads before processing any (default: false)</li>
            </ul>
          </div>

          <div class="section">
            <h2>📝 Field Descriptions</h2>
            <p>Each lead in the <code>leads</code> array follows the same structure as the single lead endpoint:</p>
            <table class="field-table">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>fullName</td>
                  <td>string</td>
                  <td><span class="required">Yes</span></td>
                  <td>Full name of the contact</td>
                </tr>
                <tr>
                  <td>email</td>
                  <td>string</td>
                  <td><span class="required">Yes</span></td>
                  <td>Email address (must be valid format)</td>
                </tr>
                <tr>
                  <td>phone</td>
                  <td>string</td>
                  <td><span class="required">Yes</span></td>
                  <td>Phone number (7-15 digits, start with 1-9, no + prefix allowed)</td>
                </tr>
                <tr>
                  <td>age</td>
                  <td>string</td>
                  <td><span class="required">Yes</span></td>
                  <td>Age of the contact</td>
                </tr>
                <tr>
                  <td>country</td>
                  <td>string</td>
                  <td><span class="required">Yes</span></td>
                  <td>Country name</td>
                </tr>
                <tr>
                  <td>marks</td>
                  <td>text</td>
                  <td><span class="required">Yes</span></td>
                  <td>Additional text data or notes about the contact</td>
                </tr>
                <tr>
                  <td>MPC_4</td>
                  <td>text</td>
                  <td><span class="required">Yes</span></td>
                  <td>Additional text data or classification</td>
                </tr>
                <tr>
                  <td>other_data</td>
                  <td>object</td>
                  <td><span class="optional">No</span></td>
                  <td>Custom data as JSON object (optional)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="section">
            <h2>💻 Example cURL Request</h2>
            <div class="code-block">
curl -X POST "https://be.blackdawn.info/api/v1/external/bulk-data-entry" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${credential.api_key}" \\
  -H "X-Username: ${credential.username || 'your_username'}" \\
  -H "X-Password: ${credential.password || 'your_password'}" \\
  -d '{
    "leads": [
      {
        "fullName": "John Doe",
        "email": "john.doe@example.com",
        "phone": "12345678900",
        "age": "30",
        "country": "United States",
        "marks": "Lead score: 85, Campaign: CAMP_001",
        "MPC_4": "High interest level",
        "other_data": {
          "lead_score": 85,
          "campaign_id": "CAMP_001",
          "interest_level": "High"
        }
      },
      {
        "fullName": "Jane Smith",
        "email": "jane.smith@example.com",
        "phone": "19876543210",
        "age": "28",
        "country": "Canada",
        "marks": "Lead score: 92, Campaign: CAMP_002",
        "MPC_4": "Very High interest level",
        "other_data": {
          "lead_score": 92,
          "campaign_id": "CAMP_002",
          "interest_level": "Very High"
        }
      }
    ],
    "batch_size": 50,
    "validate_all_first": false
  }'
            </div>
          </div>

          <div class="section">
            <h2>📤 Response Format</h2>
            <div class="code-block">
{
  "success": true,
  "summary": {
    "total_received": 100,
    "successful": 85,
    "failed": 15,
    "processing_time_ms": 1250
  },
  "results": [
    { "lead_index": 0, "success": true, "id": 12345 },
    { "lead_index": 1, "success": false, "errors": ["Invalid email format"] },
    { "lead_index": 2, "success": true, "id": 12346 }
  ],
  "failed_leads": [
    {
      "lead_index": 1,
      "data": { "fullName": "Invalid User", "email": "invalid-email" },
      "errors": ["Invalid email format"]
    }
  ],
  "message": "Bulk processing completed",
  "status": 200
}
            </div>
            <p><strong>Response Fields:</strong></p>
            <ul>
              <li><code>summary</code>: Overall statistics of the bulk operation</li>
              <li><code>results</code>: Individual result for each lead (success/failure + ID or errors)</li>
              <li><code>failed_leads</code>: Only the leads that failed validation (for easy retry)</li>
            </ul>
          </div>

          <div class="section">
            <h2>🤔 When to Use Bulk vs Single</h2>
            <div style="display: flex; gap: 20px; margin: 20px 0;">
              <div style="flex: 1; padding: 15px; background: #e8f5e8; border-radius: 5px;">
                <h3 style="color: #4caf50; margin-top: 0;">🔄 Use Single When:</h3>
                <ul style="margin: 0; padding-left: 20px;">
                  <li>Real-time integrations</li>
                  <li>Small volumes (1-10 leads)</li>
                  <li>Immediate feedback needed</li>
                  <li>Testing and development</li>
                </ul>
              </div>
              <div style="flex: 1; padding: 15px; background: #e3f2fd; border-radius: 5px;">
                <h3 style="color: #1976d2; margin-top: 0;">📦 Use Bulk When:</h3>
                <ul style="margin: 0; padding-left: 20px;">
                  <li>Batch imports (CSV uploads)</li>
                  <li>High volumes (10+ leads)</li>
                  <li>Performance critical</li>
                  <li>Multiple systems sending data</li>
                </ul>
              </div>
            </div>
          </div>

          <div class="footer">
            <p>Bulk Lead API Documentation for: ${
              credential.credential_name
            }</p>
            <p>For support, please contact your system administrator.</p>
          </div>
        </body>
        </html>
      `;
  };

  const handleDownloadApiDocumentation = async (type = 'single') => {
    try {
      setPdfLoading(true);

      // Create HTML content for the PDF based on type
      const htmlContent =
        type === 'bulk'
          ? generateBulkDocumentationHTML()
          : generateSingleDocumentationHTML();

      // Create a new window with the HTML content
      const printWindow = window.open('', '_blank');
      printWindow.document.write(htmlContent);
      printWindow.document.close();

      // Wait for content to load, then trigger print
      printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
      };
    } catch (err) {
      setError('Failed to generate PDF documentation');
      console.error('Error generating PDF:', err);
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='400px'
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity='error'>{error}</Alert>
        <Button onClick={fetchCredentialDetails} sx={{mt: 2}}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!credential) {
    return (
      <Box p={3}>
        <Alert severity='error'>Credential not found</Alert>
        <Button onClick={() => navigate('/api-credentials/all')} sx={{mt: 2}}>
          Back to Credentials
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display='flex' alignItems='center' mb={3}>
        <IconButton
          onClick={() => navigate('/api-credentials/all')}
          sx={{mr: 2}}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h4' component='h1'>
          {credential.credential_name}
        </Typography>
        <Box ml='auto' display='flex' gap={1}>
          <Button
            variant='outlined'
            startIcon={<EditIcon />}
            onClick={handleEdit}
          >
            Edit
          </Button>
          <Button
            variant='outlined'
            startIcon={credential.is_active ? <PauseIcon /> : <PlayIcon />}
            onClick={handleToggleActive}
            color={credential.is_active ? 'warning' : 'success'}
          >
            {credential.is_active ? 'Pause' : 'Activate'}
          </Button>
          <Button
            variant='outlined'
            startIcon={<ScheduleIcon />}
            onClick={handleLivePeriod}
          >
            Live Period
          </Button>
          <Button
            variant='outlined'
            color='error'
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Box>
      </Box>

      {/* Status Alert */}
      {isExpired(credential) && (
        <Alert severity='warning' sx={{mb: 3}}>
          This API credential has expired and is no longer receiving data.
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{borderBottom: 1, borderColor: 'divider', mb: 3}}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
        >
          <Tab label='Overview' />
          <Tab label='Statistics' />
          <Tab label='Report' />
          <Tab label='API Documentation' />
          <Tab label='Settings' />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {tabValue === 0 && (
        <OverviewTab
          credential={credential}
          showApiKey={showApiKey}
          setShowApiKey={setShowApiKey}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
        />
      )}

      {tabValue === 1 && (
        <StatisticsTab
          stats={stats}
          statsLoading={statsLoading}
          onRefresh={fetchStats}
        />
      )}

      {tabValue === 2 && (
        <DailyReportsTab
          dailyStats={dailyStats}
          dailyStatsLoading={dailyStatsLoading}
          pagination={pagination}
          selectedDays={selectedDays}
          setSelectedDays={setSelectedDays}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          itemsPerPage={itemsPerPage}
          handleItemsPerPageChange={handleItemsPerPageChange}
          handleSearch={handleSearch}
          handleClearFilters={handleClearFilters}
          handlePageChange={handlePageChange}
          handleExportDailyExcel={handleExportDailyExcel}
          handleDownloadDayData={handleDownloadDayData}
          exportLoading={exportLoading}
        />
      )}

      {tabValue === 3 && (
        <ApiDocumentationTab
          credential={credential}
          pdfLoading={pdfLoading}
          onDownloadPdf={handleDownloadApiDocumentation}
        />
      )}

      {tabValue === 4 && (
        <SettingsTab
          credential={credential}
          onToggleActive={handleToggleActive}
          onLivePeriod={handleLivePeriod}
        />
      )}

      {/* Dialogs */}
      <EditDialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        editForm={editForm}
        setEditForm={setEditForm}
        onSave={handleSaveEdit}
        integrationSources={integrationSources}
      />

      <DeleteDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleConfirmDelete}
      />

      <LivePeriodDialog
        open={openLivePeriodDialog}
        onClose={() => setOpenLivePeriodDialog(false)}
        livePeriodForm={livePeriodForm}
        setLivePeriodForm={setLivePeriodForm}
        onSave={handleSaveLivePeriod}
      />
    </Box>
  );
};

export default ApiCredentialDetails;
